import {
  PlanningEvent,
  PlanningStatus,
  PlanningSeverity,
} from "../models/planning-event.model";

/**
 * Mapper Facture client → PlanningEvent. Mode "parent-fetch".
 *
 * Règles produit décidées avec le métier :
 * - **EN_REDACTION** (brouillon) → skip (pas d'action datée — sera compté
 *   dans le titre du widget)
 * - **REGLEE** → skip (action terminée, disparaît du planning)
 * - **ANNULEE** → skip
 * - **Avoirs** (`isAvoir=true`) → skip (ce sont des corrections, pas des
 *   échéances à encaisser)
 * - **EMISE / PARTIELLEMENT_REGLEE / LITIGE** → affiché ancré sur
 *   `dateEcheance`. Si échéance passée & non réglée, le composant
 *   `vl-planning` applique `migrateIfLate` → tuile épinglée sur today en
 *   status `late` automatiquement.
 */
export function facturesClientToPlanningEvents(
  factures: any[],
): PlanningEvent[] {
  if (!factures?.length) return [];

  return factures
    .filter((f) => !f.isAvoir) // pas d'avoirs dans le planning
    .filter((f) => {
      // Skip états "terminaux" ou "brouillons"
      if (f.etat === "REGLEE") return false;
      if (f.etat === "ANNULEE") return false;
      if (f.etat === "EN_REDACTION") return false;
      return true;
    })
    .filter((f) => f.dateEcheance != null) // besoin d'une date d'ancrage
    .map((f) => {
      const { status, severity } = deriveStatus(f.etat);
      const client = f.tiers?.denomination ?? "";
      const numero = f.numero ?? "?";

      return {
        id: `facture-client:${f.id}`,
        source: "facture-client" as const,
        kind: "echeance",
        reference: {
          // Pas de route : le widget planning intercepte source ===
          // "facture-client" et ouvre directement le détail en dialog
          // modal (cf. WidgetPlanningComponent.onEventClick).
          label: client || numero,
        },
        datePrevu: new Date(f.dateEcheance).toISOString(),
        // Pas de dateReel (REGLEE filtrée en amont). Si partiellement réglée,
        // on reste sur l'échéance attendue — le montant résiduel est dans le
        // payload pour le tooltip.
        dateReel: null,
        status,
        severity,
        quantite: undefined,
        payload: {
          client,
          numero,
          etat: f.etat,
          etatLabel: etatToLabel(f.etat),
          totalTTC: f.totalTTC ?? null,
        },
      } satisfies PlanningEvent;
    });
}

function deriveStatus(etat: string): {
  status: PlanningStatus;
  severity: PlanningSeverity;
} {
  switch (etat) {
    case "LITIGE":
      // Nécessite action commerciale → signal fort
      return { status: "blocked", severity: 2 };
    case "PARTIELLEMENT_REGLEE":
      // Partie à relancer / encaisser
      return { status: "partial", severity: 1 };
    case "EMISE":
    default:
      // En attente de paiement — severity 1 pour remonter dans le tri
      // (les factures client concernent le cash, pas à noyer sous la masse)
      return { status: "pending", severity: 1 };
  }
}

function etatToLabel(etat: string): string {
  const labels: Record<string, string> = {
    EMISE: "Émise",
    PARTIELLEMENT_REGLEE: "Partiellement réglée",
    LITIGE: "Litige",
  };
  return labels[etat] ?? etat;
}
